import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { StreamHealthSnapshot } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';

import type { BroadcasterPreviewStats } from './BroadcasterPreview.js';
import {
  recordConnectionIssue,
  triageConnectionHealth,
  type ConnectionGuidance,
} from './connection-guidance.js';
import './StreamQualityMeter.css';

export interface StreamQualityMeterProps {
  stats: BroadcasterPreviewStats;
  viewerCount: number;
  spyCount?: number;
  health?: StreamHealthSnapshot;
}

function qualityTone(
  fps: number,
  hasVideo: boolean,
  health: StreamHealthSnapshot | undefined,
): 'good' | 'warn' | 'bad' | 'idle' {
  if (health?.severity === 'bad') {
    return 'bad';
  }
  if (health?.severity === 'warn') {
    return 'warn';
  }
  if (!hasVideo) {
    return 'idle';
  }
  if (fps >= 24) {
    return 'good';
  }
  if (fps >= 15) {
    return 'warn';
  }
  return 'bad';
}

function formatBitrate(health: StreamHealthSnapshot | undefined): string {
  if (health?.bitrateKbps === null || health?.bitrateKbps === undefined) {
    return t('common.studio.live.qualityBitratePending');
  }
  return t('common.studio.live.qualityKbps').replace(
    '{{kbps}}',
    String(Math.round(health.bitrateKbps)),
  );
}

/**
 * Local preview health row (camera/mic plus WebRTC publish to fans).
 */
export function StreamQualityMeter({
  stats,
  viewerCount,
  spyCount = 0,
  health,
}: StreamQualityMeterProps): ReactElement {
  const tone = qualityTone(stats.fps, stats.hasVideo, health);
  const resolution =
    stats.width > 0 && stats.height > 0 ? `${String(stats.width)}×${String(stats.height)}` : '—';
  const fpsLabel = stats.hasVideo ? String(stats.fps) : '—';
  const [guidance, setGuidance] = useState<ConnectionGuidance | null>(null);
  const lastSeverityRef = useRef<string | null>(null);

  useEffect(() => {
    const severity = health?.severity ?? 'idle';
    if (severity !== lastSeverityRef.current) {
      lastSeverityRef.current = severity;
      if (severity === 'warn' || severity === 'bad') {
        recordConnectionIssue(severity);
      }
    }
    setGuidance(triageConnectionHealth(health));
  }, [health]);

  return (
    <section className="stream-quality" aria-label={t('common.studio.live.qualityTitle')}>
      <header className="stream-quality__head">
        <h2 className="stream-quality__title">{t('common.studio.live.qualityTitle')}</h2>
        <span className={`stream-quality__tone stream-quality__tone--${tone}`}>
          {t(`common.studio.live.qualityTone.${tone}`)}
        </span>
      </header>
      <dl className="stream-quality__grid">
        <div>
          <dt>{t('common.studio.live.qualityResolution')}</dt>
          <dd>{resolution}</dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityFps')}</dt>
          <dd>{fpsLabel}</dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityVideo')}</dt>
          <dd>
            {stats.hasVideo
              ? t('common.studio.live.qualityOn')
              : t('common.studio.live.qualityOff')}
          </dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityAudio')}</dt>
          <dd>
            {stats.hasAudio
              ? t('common.studio.live.qualityOn')
              : t('common.studio.live.qualityOff')}
          </dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityBitrate')}</dt>
          <dd>{formatBitrate(health)}</dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityRtt')}</dt>
          <dd>
            {health?.rttMs != null
              ? t('common.studio.live.qualityRttMs').replace('{{ms}}', String(Math.round(health.rttMs)))
              : '—'}
          </dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityLoss')}</dt>
          <dd>
            {health?.packetLossPct != null
              ? t('common.studio.live.qualityLossPct').replace(
                  '{{pct}}',
                  String(Math.round(health.packetLossPct)),
                )
              : '—'}
          </dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityPeers')}</dt>
          <dd>{String(health?.connectedPeers ?? 0)}</dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityViewers')}</dt>
          <dd>{String(Math.max(0, viewerCount - spyCount))}</dd>
        </div>
        <div>
          <dt>{t('common.studio.live.qualityFansIssues')}</dt>
          <dd>{String(health?.remoteIssueCount ?? 0)}</dd>
        </div>
        {spyCount > 0 ? (
          <div>
            <dt>{t('common.studio.live.statsSpies')}</dt>
            <dd>{String(spyCount)}</dd>
          </div>
        ) : null}
      </dl>
      {guidance ? (
        <aside
          className={`stream-quality__guide stream-quality__guide--${guidance.cause}${
            guidance.isRepeat ? ' stream-quality__guide--repeat' : ''
          }`}
          role={tone === 'bad' ? 'alert' : 'status'}
        >
          <p className="stream-quality__guide-title">{guidance.title}</p>
          <p className="stream-quality__guide-body">{guidance.body}</p>
        </aside>
      ) : null}
    </section>
  );
}
